<template>
  <FlashNotifications ref="flash" />

  <!-- Delete confirmation modal -->
  <div v-if="showDeleteConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
    <div class="bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
      <div class="flex items-center gap-3 mb-3">
        <div class="w-10 h-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
          <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
          </svg>
        </div>
        <div>
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Delete {{ selectedIds.length }} email{{ selectedIds.length === 1 ? '' : 's' }}?</h3>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This action cannot be undone.</p>
        </div>
      </div>
      <div class="flex gap-2 justify-end mt-4">
        <button
          @click="showDeleteConfirm = false"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition"
        >Cancel</button>
        <button
          @click="confirmBulkDelete"
          :disabled="deleting"
          class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-lg transition flex items-center gap-1.5"
        >
          <svg v-if="deleting" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          Delete
        </button>
      </div>
    </div>
  </div>

  <!-- Desktop layout -->
  <div class="hidden sm:flex sm:flex-col bg-gray-50 dark:bg-[#0d1117]" style="height: calc(100vh - 57px)">

    <!-- Slim header -->
    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5 gap-4">
        <div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white leading-none">Emails</h1>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Monitor all outgoing emails and open tracking</p>
        </div>
        <div class="flex items-center gap-3">
          <!-- Bulk delete button -->
          <button
            v-if="selectedIds.length"
            @click="showDeleteConfirm = true"
            class="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
            </svg>
            Delete {{ selectedIds.length }}
          </button>
          <!-- Search -->
          <div class="relative">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
            <input
              v-model="search"
              @input="onSearchInput"
              placeholder="Search by subject, sender, recipient…"
              class="pl-9 pr-4 py-2 w-72 text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>
          <!-- Refresh -->
          <button
            @click="fetchEmailsList"
            class="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- Two-panel body -->
    <div class="flex-1 flex overflow-hidden">

      <!-- Left: email list -->
      <div class="w-80 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">

        <!-- Select-all bar (shown when list is loaded and non-empty) -->
        <div v-if="!loading && emails.length" class="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50 dark:bg-[#161b22]">
          <input
            type="checkbox"
            :checked="allSelected"
            :indeterminate.prop="someSelected"
            @change="toggleSelectAll"
            class="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
          <span class="text-[11px] text-gray-500 dark:text-gray-400 select-none">
            {{ selectedIds.length ? `${selectedIds.length} selected` : 'Select all' }}
          </span>
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading" class="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60 animate-pulse">
          <div v-for="n in 8" :key="n" class="p-4 space-y-2.5">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
              <div class="flex-1 space-y-1.5">
                <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
              </div>
              <div class="h-4 w-14 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
            </div>
            <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
          </div>
        </div>

        <!-- Email list -->
        <div v-else ref="emailList" class="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
          <div
            v-for="email in emails"
            :key="email.id"
            data-email-row
            @click="selectEmail(email)"
            class="group relative p-4 cursor-pointer transition-colors"
            :class="[
              selectedIds.includes(email.id)
                ? 'bg-red-50 dark:bg-red-900/10'
                : email.id === selected_email.id
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
            ]"
          >
            <!-- Active indicator -->
            <div
              v-if="email.id === selected_email.id && !selectedIds.length"
              class="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-600 rounded-r"
            ></div>

            <!-- Selected indicator -->
            <div
              v-if="selectedIds.includes(email.id)"
              class="absolute left-0 top-0 bottom-0 w-0.5 bg-red-500 rounded-r"
            ></div>

            <div class="flex items-start gap-3">
              <!-- Checkbox -->
              <div class="flex-shrink-0 mt-0.5" @click.stop>
                <input
                  type="checkbox"
                  :checked="selectedIds.includes(email.id)"
                  @change="toggleSelect(email.id)"
                  class="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 cursor-pointer"
                />
              </div>

              <!-- Sender avatar -->
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                :style="avatarStyle(email)"
              >
                {{ avatarInitials(email) }}
              </div>

              <div class="flex-1 min-w-0">
                <!-- Top row: sender + status badge -->
                <div class="flex items-center justify-between gap-2 mb-0.5">
                  <span class="text-[12.5px] font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {{ senderName(email) }}
                  </span>
                  <span
                    v-if="email.email_tracking_histories?.length"
                    class="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700/50 rounded-full px-1.5 py-0.5"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></span>
                    Opened
                  </span>
                  <span
                    v-else
                    class="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-full px-1.5 py-0.5"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-500 flex-shrink-0"></span>
                    Pending
                  </span>
                </div>

                <!-- Subject -->
                <p
                  class="text-[12px] font-medium truncate mb-1"
                  :class="email.id === selected_email.id && !selectedIds.length ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'"
                >
                  {{ email.subject }}
                </p>

                <!-- To + Date -->
                <div class="flex items-center justify-between gap-2">
                  <span class="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                    To: {{ recipientDisplay(email) }}
                  </span>
                  <span class="text-[10.5px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {{ email.formatted_created_at }}
                  </span>
                </div>
              </div>
            </div>
          </div>

            <!-- Empty state -->
          <div v-if="!emails.length && !loading" class="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
              <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"/>
              </svg>
            </div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">No emails found</p>
            <p v-if="search" class="text-xs text-gray-400 dark:text-gray-500 mt-1">Try a different search term</p>
          </div>

          <!-- Infinite scroll sentinel + spinner -->
          <div ref="sentinel" class="px-4 py-3 flex items-center justify-center">
            <div v-if="loadingMore" class="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
              <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              Loading more…
            </div>
            <div v-else-if="page.current_page >= page.total_pages && emails.length" class="text-[11px] text-gray-300 dark:text-gray-600">
              All caught up
            </div>
          </div>
        </div>
      </div>

      <!-- Right: preview panel -->
      <div class="flex-1 flex flex-col overflow-hidden">

        <!-- Nothing selected -->
        <div v-if="!selected_email.id" class="flex-1 flex flex-col items-center justify-center text-center px-8">
          <div class="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"/>
            </svg>
          </div>
          <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400">Select an email to preview</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Click any email in the list on the left</p>
        </div>

        <!-- Email preview -->
        <template v-else>
          <!-- Metadata bar -->
          <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div class="flex items-start justify-between gap-6">

              <!-- Email metadata -->
              <div class="flex-1 min-w-0 space-y-2">
                <h2 class="text-base font-semibold text-gray-900 dark:text-white truncate">{{ selected_email.subject }}</h2>
                <div class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
                  <span v-if="selected_email.from?.length" class="text-gray-400 dark:text-gray-500 font-medium self-start pt-px">From</span>
                  <span v-if="selected_email.from?.length" class="text-gray-700 dark:text-gray-200">{{ selected_email.from.join(', ') }}</span>

                  <span v-if="selected_email.to?.length" class="text-gray-400 dark:text-gray-500 font-medium self-start pt-px">To</span>
                  <span v-if="selected_email.to?.length" class="text-gray-700 dark:text-gray-200">{{ selected_email.to.join(', ') }}</span>

                  <span v-if="selected_email.reply_to?.length" class="text-gray-400 dark:text-gray-500 font-medium self-start pt-px">Reply-to</span>
                  <span v-if="selected_email.reply_to?.length" class="text-gray-700 dark:text-gray-200">{{ selected_email.reply_to.join(', ') }}</span>

                  <span class="text-gray-400 dark:text-gray-500 font-medium">Sent</span>
                  <span class="text-gray-700 dark:text-gray-200">{{ selected_email.formatted_created_at }}</span>
                </div>
              </div>

              <!-- Right column: opens + cursor toggle -->
              <div class="flex-shrink-0 flex flex-col items-end gap-3">

                <!-- Open tracking -->
                <div class="flex flex-col items-end gap-1">
                  <div v-if="selected_email.email_tracking_histories?.length" class="flex items-center gap-2">
                    <span class="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0"></span>
                    <span class="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                      Opened {{ selected_email.email_tracking_histories.length }}×
                    </span>
                  </div>
                  <div v-else class="flex items-center gap-2">
                    <span class="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0"></span>
                    <span class="text-xs font-semibold text-gray-400 dark:text-gray-500">Not opened yet</span>
                  </div>

                  <!-- Open timestamps -->
                  <div v-if="selected_email.email_tracking_histories?.length" class="text-right max-h-16 overflow-y-auto space-y-0.5">
                    <p
                      v-for="(t, i) in selected_email.email_tracking_histories"
                      :key="t.id"
                      class="text-[10.5px] text-gray-400 dark:text-gray-500"
                    >
                      {{ i === 0 ? 'First' : `#${i + 1}` }}: {{ t.formatted_created_at }}
                    </p>
                  </div>
                </div>

                <!-- Cursor toggle -->
                <label class="flex items-center gap-2 cursor-pointer select-none">
                  <span class="text-[11px] font-medium text-gray-500 dark:text-gray-400">Enable cursor</span>
                  <div class="relative">
                    <input type="checkbox" class="sr-only peer" v-model="enable_email">
                    <div class="w-9 h-5 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
                    <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- iframe -->
          <div class="flex-1 overflow-hidden bg-gray-100 dark:bg-[#0d1117]">
            <iframe
              :src="selected_email.iframe_url"
              class="w-full h-full border-0"
              :class="{ 'pointer-events-none': !enable_email }"
            ></iframe>
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Mobile layout -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <!-- Sticky header -->
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Emails</h1>
        <div class="flex items-center gap-2">
          <button
            v-if="selectedIds.length"
            @click="showDeleteConfirm = true"
            class="px-3 py-1.5 bg-red-600 text-white text-sm font-semibold rounded-full"
          >Delete {{ selectedIds.length }}</button>
          <button @click="fetchEmailsList" class="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">Refresh</button>
        </div>
      </div>
      <div class="px-4 pb-3">
        <div class="relative">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input
            v-model="search"
            @input="onSearchInput"
            placeholder="Search emails…"
            class="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
    </div>

    <!-- Email cards -->
    <div class="pb-24 pt-2">
      <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
        <div
          v-for="email in emails"
          :key="email.id"
          class="p-4 cursor-pointer"
          :class="selectedIds.includes(email.id) ? 'bg-red-50 dark:bg-red-900/10' : email.id === selected_email.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''"
          @click="selectEmail(email)"
        >
          <div class="flex items-start gap-3">
            <!-- Checkbox -->
            <div class="flex-shrink-0 mt-1" @click.stop>
              <input
                type="checkbox"
                :checked="selectedIds.includes(email.id)"
                @change="toggleSelect(email.id)"
                class="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 cursor-pointer"
              />
            </div>
            <div class="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0" :style="avatarStyle(email)">
              {{ avatarInitials(email) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between mb-0.5">
                <span class="text-[13px] font-semibold text-gray-800 dark:text-gray-100 truncate">{{ senderName(email) }}</span>
                <span v-if="email.email_tracking_histories?.length" class="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700/50 rounded-full px-1.5 py-0.5 flex-shrink-0 ml-2">Opened</span>
                <span v-else class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-1.5 py-0.5 flex-shrink-0 ml-2">Pending</span>
              </div>
              <p class="text-xs font-medium text-gray-700 dark:text-gray-200 truncate mb-1">{{ email.subject }}</p>
              <p class="text-[11px] text-gray-400 dark:text-gray-500">{{ email.formatted_created_at }}</p>
            </div>
          </div>

          <!-- Expanded preview on mobile -->
          <div v-if="email.id === selected_email.id" class="mt-3 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700" style="height: 60vh">
            <iframe :src="selected_email.iframe_url" class="w-full h-full border-0 pointer-events-none"></iframe>
          </div>
        </div>

        <div v-if="!emails.length" class="flex flex-col items-center justify-center py-16 text-center">
          <p class="text-sm text-gray-400 dark:text-gray-500">No emails found</p>
        </div>
      </div>

      <!-- Mobile infinite scroll sentinel -->
      <div ref="mobileSentinel" class="px-4 py-4 flex items-center justify-center">
        <div v-if="loadingMore" class="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          Loading more…
        </div>
        <div v-else-if="page.current_page >= page.total_pages && emails.length" class="text-[11px] text-gray-300 dark:text-gray-600">
          All caught up
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RequestMixin from "controllers/vue_components/mixins/request"
import Constants from "controllers/vue_components/mixins/constants"
import Formatting from "controllers/vue_components/mixins/formatting"
import FlashNotifications from "controllers/vue_components/flash_toast"

const AVATAR_COLORS = [
  ['#dbeafe', '#1d4ed8'], ['#dcfce7', '#15803d'], ['#fef9c3', '#a16207'],
  ['#fce7f3', '#be185d'], ['#ede9fe', '#6d28d9'], ['#ffedd5', '#c2410c'],
]

function pickColor(str) {
  let h = 0
  for (let i = 0; i < (str || '').length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

export default {
  name: 'AdminEmails',
  components: { FlashNotifications },
  mixins: [RequestMixin, Constants, Formatting],
  data() {
    return {
      loading: false,
      loadingMore: false,
      deleting: false,
      page: { limit: 20, number: 1, current_page: 1, total_pages: 1 },
      emails: [],
      search: '',
      selected_email: {},
      enable_email: false,
      selectedIds: [],
      showDeleteConfirm: false,
      _observer: null,
      _searchTimeout: null,
    }
  },
  computed: {
    hasMore() {
      return this.page.current_page < this.page.total_pages
    },
    allSelected() {
      return this.emails.length > 0 && this.selectedIds.length === this.emails.length
    },
    someSelected() {
      return this.selectedIds.length > 0 && this.selectedIds.length < this.emails.length
    },
  },
  methods: {
    // Fresh load — resets the list (used on search / refresh)
    async fetchEmailsList() {
      this.loading = true
      this.page.number = 1
      this.selectedIds = []
      try {
        const payload = { page: { limit: this.page.limit, number: 1 }, filters: {} }
        if (this.search) payload.search = this.search
        const response = await this.axiosRequest('POST', '/api/v1/admin/emails/list', payload, false)
        if (response.js_error == false && response.response.success) {
          this.emails = response.response.data.records
          this.page = { ...this.page, ...response.response.data.page, number: response.response.data.page.current_page }
          if (!this.selected_email.id && this.emails.length) this.selected_email = this.emails[0]
        }
      } finally {
        this.loading = false
      }
    },

    // Append-load — used by infinite scroll
    async loadNextPage() {
      if (this.loadingMore || !this.hasMore) return
      this.loadingMore = true
      try {
        const nextNum = this.page.current_page + 1
        const payload = { page: { limit: this.page.limit, number: nextNum }, filters: {} }
        if (this.search) payload.search = this.search
        const response = await this.axiosRequest('POST', '/api/v1/admin/emails/list', payload, false)
        if (response.js_error == false && response.response.success) {
          this.emails.push(...response.response.data.records)
          this.page = { ...this.page, ...response.response.data.page, number: response.response.data.page.current_page }
        }
      } finally {
        this.loadingMore = false
      }
    },

    onSearchInput() {
      clearTimeout(this._searchTimeout)
      this._searchTimeout = setTimeout(() => this.fetchEmailsList(), 300)
    },

    selectEmail(email) {
      this.selected_email = email
    },

    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx === -1) {
        this.selectedIds.push(id)
      } else {
        this.selectedIds.splice(idx, 1)
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds = []
      } else {
        this.selectedIds = this.emails.map(e => e.id)
      }
    },

    async confirmBulkDelete() {
      this.deleting = true
      try {
        const response = await this.axiosRequest('DELETE', '/api/v1/admin/emails/bulk_destroy', { ids: this.selectedIds }, false)
        if (response.js_error == false && response.response.success) {
          const deletedIds = new Set(this.selectedIds)
          this.emails = this.emails.filter(e => !deletedIds.has(e.id))
          if (deletedIds.has(this.selected_email.id)) {
            this.selected_email = this.emails[0] || {}
          }
          this.selectedIds = []
          this.showDeleteConfirm = false
          this.$refs.flash?.show(`${response.response.data.deleted_count} email(s) deleted`, 'success')
        } else {
          this.$refs.flash?.show('Failed to delete emails', 'error')
        }
      } finally {
        this.deleting = false
      }
    },

    senderName(email) {
      const raw = email.from?.[0] || ''
      const match = raw.match(/^"?([^"<]+)"?\s*<?/)
      return match?.[1]?.trim() || raw
    },
    recipientDisplay(email) {
      if (!email.to?.length) return '—'
      const first = email.to[0]
      const match = first.match(/<([^>]+)>/)
      return match ? match[1] : first
    },
    avatarInitials(email) {
      const name = this.senderName(email)
      const parts = name.trim().split(/\s+/)
      return parts.length > 1
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase()
    },
    avatarStyle(email) {
      const [bg, text] = pickColor(this.senderName(email))
      return { background: bg, color: text }
    },

    setupObserver() {
      this._observer = new IntersectionObserver(
        (entries) => { if (entries[0].isIntersecting) this.loadNextPage() },
        { threshold: 0.1 }
      )
      if (this.$refs.sentinel) this._observer.observe(this.$refs.sentinel)
      if (this.$refs.mobileSentinel) this._observer.observe(this.$refs.mobileSentinel)
    },

    onKeyDown(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      if (!this.emails.length) return
      // Don't steal keystrokes while the user is typing in the search box
      if (document.activeElement?.tagName === 'INPUT') return
      e.preventDefault()
      const idx = this.emails.findIndex(em => em.id === this.selected_email.id)
      const next = e.key === 'ArrowDown'
        ? Math.min(idx + 1, this.emails.length - 1)
        : Math.max(idx - 1, 0)
      if (next === idx) return
      this.selected_email = this.emails[next]
      this.$nextTick(() => {
        const rows = this.$refs.emailList?.querySelectorAll('[data-email-row]')
        rows?.[next]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    },
  },

  async created() {
    await this.fetchEmailsList()
  },

  mounted() {
    this.setupObserver()
    document.addEventListener('keydown', this.onKeyDown)
  },

  beforeUnmount() {
    this._observer?.disconnect()
    clearTimeout(this._searchTimeout)
    document.removeEventListener('keydown', this.onKeyDown)
  },
}
</script>
